/**
 * Modal Component
 * 
 * A production-quality, accessible modal dialog component with portal rendering,
 * focus management, and keyboard support.
 * 
 * @example
 * ```jsx
 * import React, { useState, useRef } from 'react';
 * import Modal from './components/ui/Modal';
 * 
 * function MyComponent() {
 *   const [isOpen, setIsOpen] = useState(false);
 *   const triggerRef = useRef(null);
 * 
 *   return (
 *     <>
 *       <button ref={triggerRef} onClick={() => setIsOpen(true)}>
 *         Learn More
 *       </button>
 *       <Modal
 *         isOpen={isOpen}
 *         onClose={() => setIsOpen(false)}
 *         title="About This Feature"
 *         primaryAction={{
 *           label: 'Get Started',
 *           href: 'https://example.com',
 *           target: '_blank'
 *         }}
 *       >
 *         <p>This is the modal content. You can include any JSX here.</p>
 *       </Modal>
 *     </>
 *   );
 * }
 * ```
 * 
 * @param {Object} props
 * @param {boolean} props.isOpen - Controls modal visibility
 * @param {function} props.onClose - Callback to close the modal
 * @param {string} props.title - Modal heading text
 * @param {ReactNode} props.children - Modal body content
 * @param {Object} [props.primaryAction] - Primary CTA button config
 * @param {string} props.primaryAction.label - Button text
 * @param {string} [props.primaryAction.href] - Link URL (renders as <a>)
 * @param {function} [props.primaryAction.onClick] - Click handler (renders as <button>)
 * @param {string} [props.primaryAction.target] - Link target attribute
 * @param {Object} [props.secondaryAction] - Secondary action button config
 * @param {string} props.secondaryAction.label - Button text
 * @param {function} props.secondaryAction.onClick - Click handler
 * @param {boolean} [props.closeOnOverlayClick=true] - Close on overlay click
 * @param {string} [props.id] - Optional ID for ARIA attributes
 */

import React, { useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import './Modal.css';

const Modal = ({
    isOpen,
    onClose,
    title,
    children,
    primaryAction,
    secondaryAction,
    closeOnOverlayClick = true,
    id = 'modal'
}) => {
    const modalRef = useRef(null);
    const previousActiveElement = useRef(null);
    const titleId = `${id}-title`;
    const descId = `${id}-desc`;

    // Handle Escape key
    const handleEscape = useCallback((event) => {
        if (event.key === 'Escape') {
            onClose();
        }
    }, [onClose]);

    // Handle overlay click
    const handleOverlayClick = useCallback((event) => {
        if (closeOnOverlayClick && event.target === event.currentTarget) {
            onClose();
        }
    }, [closeOnOverlayClick, onClose]);

    // Focus trap implementation
    const handleTabKey = useCallback((event) => {
        if (!modalRef.current) return;

        const focusableElements = modalRef.current.querySelectorAll(
            'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        const firstElement = focusableElements[0];
        const lastElement = focusableElements[focusableElements.length - 1];

        if (event.shiftKey) {
            // Shift + Tab
            if (document.activeElement === firstElement) {
                event.preventDefault();
                lastElement?.focus();
            }
        } else {
            // Tab
            if (document.activeElement === lastElement) {
                event.preventDefault();
                firstElement?.focus();
            }
        }
    }, []);

    const handleKeyDown = useCallback((event) => {
        if (event.key === 'Tab') {
            handleTabKey(event);
        }
    }, [handleTabKey]);

    // Manage focus and body scroll
    useEffect(() => {
        if (isOpen) {
            // Store the currently focused element
            previousActiveElement.current = document.activeElement;

            // Prevent body scroll
            const originalOverflow = document.body.style.overflow;
            document.body.style.overflow = 'hidden';

            // Add event listeners
            document.addEventListener('keydown', handleEscape);
            document.addEventListener('keydown', handleKeyDown);

            // Focus the modal
            setTimeout(() => {
                modalRef.current?.focus();
            }, 0);

            // Cleanup
            return () => {
                document.body.style.overflow = originalOverflow;
                document.removeEventListener('keydown', handleEscape);
                document.removeEventListener('keydown', handleKeyDown);

                // Return focus to the trigger element
                if (previousActiveElement.current && typeof previousActiveElement.current.focus === 'function') {
                    previousActiveElement.current.focus();
                }
            };
        }
    }, [isOpen, handleEscape, handleKeyDown]);

    // Get or create modal root
    const getModalRoot = () => {
        let modalRoot = document.getElementById('modal-root');
        if (!modalRoot) {
            modalRoot = document.createElement('div');
            modalRoot.id = 'modal-root';
            document.body.appendChild(modalRoot);
        }
        return modalRoot;
    };

    if (!isOpen) return null;

    // Ensure we're in a browser environment
    if (typeof document === 'undefined') return null;

    const modalContent = (
        <div className="modal-overlay" onClick={handleOverlayClick}>
            <div
                ref={modalRef}
                className="modal-container"
                role="dialog"
                aria-modal="true"
                aria-labelledby={titleId}
                aria-describedby={descId}
                tabIndex={-1}
            >
                <div className="modal-header">
                    <h2 id={titleId} className="modal-title">{title}</h2>
                    <button
                        className="modal-close"
                        onClick={onClose}
                        aria-label="Close modal"
                        type="button"
                    >
                        <span aria-hidden="true">&times;</span>
                    </button>
                </div>

                <div id={descId} className="modal-body">
                    {children}
                </div>

                {(primaryAction || secondaryAction) && (
                    <div className="modal-footer">
                        {secondaryAction && (
                            <button
                                className="modal-button modal-button--secondary"
                                onClick={secondaryAction.onClick}
                                type="button"
                            >
                                {secondaryAction.label}
                            </button>
                        )}
                        {primaryAction && (
                            primaryAction.href ? (
                                <a
                                    className="modal-button modal-button--primary"
                                    href={primaryAction.href}
                                    target={primaryAction.target}
                                    rel={primaryAction.target === '_blank' ? 'noopener noreferrer' : undefined}
                                    onClick={primaryAction.onClick}
                                >
                                    {primaryAction.label}
                                </a>
                            ) : (
                                <button
                                    className="modal-button modal-button--primary"
                                    onClick={primaryAction.onClick}
                                    type="button"
                                >
                                    {primaryAction.label}
                                </button>
                            )
                        )}
                    </div>
                )}
            </div>
        </div>
    );

    return createPortal(modalContent, getModalRoot());
};

export default Modal;
